/**
 * The guides, as a public shop window.
 *
 * Titles and one-line summaries only, straight from the same ten guides the
 * app ships with — a prospect can see the depth of what is documented, and
 * reading it is one of the things they get when they sign in. The bodies are
 * never sent to this page; the API returns briefs.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, LifeBuoy, Sparkles } from 'lucide-react';
import { API } from '../../lib/api';
import { MERCHANT, POLICY_LINKS } from './legal';

const DrivelineGuides = () => {
  const [guides, setGuides] = useState([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    fetch(`${API}/public/driveline/guides`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { if (live) setGuides(d.guides || []); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);

  return (
    <div className="driveline-scope min-h-screen bg-[#FAFAF8] text-slate-900" data-testid="driveline-guides">
      <section className="pt-32 pb-12 sm:pt-36">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Link
            to="/driveline"
            className="text-xs uppercase tracking-[0.18em] text-teal-700 hover:text-teal-900"
            data-testid="guides-back"
          >
            Driveline
          </Link>
          <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight max-w-2xl leading-[1.08]">
            Every part of it is written down.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-500 leading-relaxed max-w-2xl">
            Ten guides ship inside Driveline, covering the work from your first
            week to your GST returns. Here is what each one answers. The guides
            themselves — and an assistant that answers from them in plain
            English — open from the Help button once you are signed in.
          </p>
        </div>
      </section>

      <section className="pb-6">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          {failed && (
            <p className="text-sm text-slate-500" data-testid="guides-failed">
              The guide list is not loading just now. Everything in it is still in
              the app — or write to{' '}
              <a className="text-teal-700 underline" href={`mailto:${MERCHANT.email}`}>{MERCHANT.email}</a>.
            </p>
          )}
          <div className="grid gap-px bg-slate-200 sm:grid-cols-2 rounded-2xl overflow-hidden border border-slate-200">
            {guides.map((g, i) => (
              <article
                key={g.slug}
                className="bg-[#FAFAF8] p-6 sm:p-7"
                data-testid={`public-guide-${g.slug}`}
              >
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-slate-400">
                  <BookOpen size={12} className="text-teal-700" />
                  Guide {String(i + 1).padStart(2, '0')}
                </div>
                <h2 className="mt-3 text-lg font-semibold tracking-tight">{g.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{g.summary}</p>
              </article>
            ))}
          </div>
          {guides.length > 0 && (
            <p className="mt-4 text-xs text-slate-400" data-testid="guides-count">
              {guides.length} guides, kept in step with the software — they ship with
              the features they describe.
            </p>
          )}
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-7 sm:p-9">
            <h2 className="text-2xl font-semibold tracking-tight">Help that sits in the app, not in a PDF</h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-3">
              <div>
                <Sparkles size={18} className="text-teal-700" />
                <h3 className="mt-3 text-base font-semibold">An assistant</h3>
                <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">
                  Ask how to do something, or where your own numbers stand. It reads
                  your workshop and never changes anything.
                </p>
              </div>
              <div>
                <BookOpen size={18} className="text-teal-700" />
                <h3 className="mt-3 text-base font-semibold">The guides in full</h3>
                <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">
                  Searchable, with the exact screen and the exact tick box — not a
                  general description of the feature.
                </p>
              </div>
              <div>
                <LifeBuoy size={18} className="text-teal-700" />
                <h3 className="mt-3 text-base font-semibold">A real person</h3>
                <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">
                  When neither lands, one form reaches {MERCHANT.legal} with your
                  conversation attached, so you never explain twice.
                </p>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/get-started"
                className="inline-flex items-center gap-2 rounded-full bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-teal-700"
                data-testid="guides-trial-cta"
              >
                Start the 14-day trial <ArrowRight size={15} />
              </Link>
              <Link
                to="/driveline/pricing"
                className="text-sm font-medium text-slate-600 underline-offset-4 hover:text-teal-700 hover:underline"
                data-testid="guides-pricing-link"
              >
                See pricing
              </Link>
            </div>
          </div>

          <nav className="mt-10 flex flex-wrap gap-x-5 gap-y-2" data-testid="guides-nav">
            {POLICY_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="text-sm text-slate-500 hover:text-teal-700 underline-offset-4 hover:underline">
                {l.label}
              </Link>
            ))}
          </nav>
        </div>
      </section>
    </div>
  );
};

export default DrivelineGuides;
